import { createAsyncThunk } from "@reduxjs/toolkit";
import { displayAlert } from "../slice/alertSlice.tsx";
import profileApi from "../api/profileApi.tsx";
import { setCountries, setStates } from "../slice/profileSlice.tsx";
import {
  ChangeEmailBodyType,
  ChangePasswordDataType,
  ProfileBodyDataType,
  ProfileResponseDataType,
} from "../types/profileTypes.tsx";
import { getStorageName } from "../../helpers/storageName/storageName.ts";
import { setToken } from "../slice/userAuthSlice.tsx";
import { AxiosError, AxiosResponse, isAxiosError } from "axios";
import { handleThunkError } from "./handleThunkError";
import { fetchReasonsForHide } from "./creatorsThunk.tsx";

interface ApiErrorList {
  [key: string]: string[];
}

export const fetchProfileData = createAsyncThunk(
  "profile/fetchProfileData",
  async (_, thunkAPI) => {
    try {
      const response: AxiosResponse<ProfileResponseDataType> =
        await profileApi.fetchProfileData();
      thunkAPI.dispatch(setCountries(response.data.meta.countries));
      thunkAPI.dispatch(setStates(response.data.meta.states));
      thunkAPI.dispatch(fetchReasonsForHide());
      return response.data.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to fetch profile data.");
      return thunkAPI.rejectWithValue("Failed to fetch profile data.");
    }
  },
);

export const editProfileData = createAsyncThunk(
  "profile/editProfileData",
  async (data: ProfileBodyDataType, thunkAPI) => {
    try {
      const response = await profileApi.editProfileData(data.body);
      if (data.callback) {
        data.callback();
      }
      thunkAPI.dispatch(setCountries(response.data.meta.countries));
      thunkAPI.dispatch(setStates(response.data.meta.states));
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Profile was edited successfully",
        }),
      );
      return response.data.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) return;
      if (
        e instanceof AxiosError &&
        e.response?.data?.length > 0 &&
        e.response
      ) {
        const data = e.response.data as ApiErrorList;
        Object.keys(data).forEach((key: string) =>
          thunkAPI.dispatch(
            displayAlert({
              type: "error",
              text: data[key].join(". "),
            }),
          ),
        );
      }
    }
  },
);

export const changeEmailProfileData = createAsyncThunk(
  "profile/changeEmailProfileData",
  async (body: ChangeEmailBodyType, thunkAPI) => {
    try {
      const response = await profileApi.changeEmailProfileData(body);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: response.data.detail,
        }),
      );
      const logOut = () => {
        localStorage.removeItem(getStorageName.refreshToken);
        localStorage.removeItem(getStorageName.accessToken);
        thunkAPI.dispatch(setToken(""));
      };
      setTimeout(logOut, 2000);
      return body.new_email;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to change profile email.");
    }
  },
);
export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async (data: ChangePasswordDataType, thunkAPI) => {
    try {
      const response = await profileApi.changePassword(data.body);
      if (data.callback) {
        data.callback();
      }
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: response.data.detail,
        }),
      );
      const logOut = () => {
        localStorage.removeItem(getStorageName.refreshToken);
        localStorage.removeItem(getStorageName.accessToken);
        thunkAPI.dispatch(setToken(""));
      };
      setTimeout(logOut, 2000);
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429) return;
      if (isAxiosError(e) && e.response?.data) {
        const data = e.response.data as ApiErrorList;
        Object.keys(data).forEach((key: string) =>
          thunkAPI.dispatch(
            displayAlert({
              type: "error",
              text: data[key].join(". "),
            }),
          ),
        );
      }
    }
  },
);

export const fetchStates = createAsyncThunk(
  "profile/fetchStates",
  async (id: number, thunkAPI) => {
    try {
      const response = await profileApi.fetchStates(id);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to fetch states.");
    }
  },
);

export const fetchPlatforms = createAsyncThunk(
  "profile/fetchPlatforms",
  async (_, thunkAPI) => {
    try {
      const response = await profileApi.fetchPlatforms();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to fetch platforms.");
    }
  },
);
